import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit, signal } from '@angular/core';
import { CUSTOMER_PERSON_API_SERVICE_TOKEN, SCREENING_CONFIG_API_SERVICE_TOKEN } from '@nk/api-lib';
import { LOADING_STATE_HANDLER_SERVICE_TOKEN } from '@nk/utils-lib';
import { ScreeningCaseCompositeEntity } from '@nk/data-entities-lib';
import { NkCardListItemDetails, ProgressComponent } from '@nk/ui-lib';
import { catchError, finalize, map, Observable, of } from 'rxjs';
import { ScreeningCaseService } from '../../services/screening-case.service';
import { ScreeningInputParamsService } from '../../services/screening-input-params.service';
import { MonitoringConnectionCardListItem } from '../../utils/monitoring-connection.utils';
import { ScreeningCardListDetailsInputData } from '../../utils/screening.utils';
import { PersonCommonInfoComponent } from '../person-common-info/person-common-info.component';
import { PersonCompanyRolesComponent } from '../person-company-roles/person-company-roles.component';
import { PersonOwnershipComponent } from '../person-ownership/person-ownership.component';
import { ScreeningActionCardListComponent } from '../screening-action-card-list/screening-action-card-list.component';
import { ScreeningAdverseMediaCardListComponent } from '../screening-adverse-media-card-list/screening-adverse-media-card-list.component';
import { ScreeningCaseTableComponent } from '../screening-case-table/screening-case-table.component';


@Component({
  selector: 'nk-monitoring-connection-details',
  imports: [
    AsyncPipe,
    PersonCommonInfoComponent,
    PersonOwnershipComponent,
    PersonCompanyRolesComponent,
    ScreeningActionCardListComponent,
    ScreeningAdverseMediaCardListComponent,
    ScreeningCaseTableComponent,
    ProgressComponent,
  ],
  templateUrl: './monitoring-connection-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonitoringConnectionDetailsComponent
  implements NkCardListItemDetails<MonitoringConnectionCardListItem>, OnInit
{
  @Input() public entity!: MonitoringConnectionCardListItem;
  public inputData = signal<ScreeningCardListDetailsInputData | null>(null);
  public screeningCaseComposites = signal<ScreeningCaseCompositeEntity[]>([]);
  public screeningEntityUuid$!: Observable<string | undefined>;
  public isLoadingScreeningEntityUuid = signal(false);
  public isPrintVersion = !!inject(LOADING_STATE_HANDLER_SERVICE_TOKEN, { optional: true });
  private _customerPersonService = inject(CUSTOMER_PERSON_API_SERVICE_TOKEN);
  private _screeningConfigService = inject(SCREENING_CONFIG_API_SERVICE_TOKEN);
  private _screeningCaseService = inject(ScreeningCaseService);
  private _screeningInputParamsService = inject(ScreeningInputParamsService);

  public ngOnInit(): void {
    const person = this.entity.data.person;
    const companyUuid = this._screeningInputParamsService.screeningInputParams()?.companyUuid;
    if (person && companyUuid) {
      this.inputData.set({
        uuid: person.uuid,
        type: 'person',
        monitoring_origin_entity_company_uuid: companyUuid,
        person_master_uuid: person.person_master_uuid,
        birth_date: person.birth_date,
        birth_year: person.birth_year,
        national_identification_schema: person.national_identification_schema,
        street_address: person.street_address,
        postal_code: person.postal_code,
        postal_place: person.postal_place,
        municipality_name: person.municipality_name,
        country_name: person.country_name,
      });
    }
    const customerPerson = this.entity.data.customerPerson;
    if (customerPerson) {
      const screeningConfigUuid = this.entity.data.screeningConfigUuid;
      if (screeningConfigUuid) {
        this._customerPersonService.customerPersonCompositeCache
          .get(screeningConfigUuid, customerPerson.uuid)
          .subscribe((composite) => {
            const customerPersonAddress = composite.customer_person_locations[0];
            this.inputData.set({
              uuid: customerPerson.uuid,
              type: 'customer_person',
              monitoring_origin_entity_company_uuid: null,
              person_master_uuid: null,
              birth_date: customerPerson.birth_date,
              birth_year: null,
              national_identification_schema:
                composite.customer_person_national_identifications[0]?.national_identification_schema ?? null,
              street_address: customerPersonAddress?.street_address,
              postal_code: customerPersonAddress?.postal_code,
              postal_place: customerPersonAddress?.postal_place,
              municipality_name: customerPersonAddress?.municipality_name,
              country_name: customerPersonAddress?.country_name,
            });
          });
      }
    }

    const screeningConfigUuid = this.entity.data.screeningConfigUuid;
    const screeningEntityUuid = this.entity.data.screeningEntityUuid;

    if (screeningConfigUuid && screeningEntityUuid) {
      this._screeningCaseService.screeningEntityCaseCompositeCollectionCache
        .get(screeningConfigUuid, screeningEntityUuid, 100, 0)
        .pipe(catchError(() => of({ elements: [] })))
        .subscribe((collection) => this.screeningCaseComposites.set(collection.elements));
    }

    if (screeningEntityUuid) {
      this.screeningEntityUuid$ = of(screeningEntityUuid);
    } else {
      this.isLoadingScreeningEntityUuid.set(true);
      this.screeningEntityUuid$ = this._screeningConfigService.screeningEntityListCache
        .get(this.entity.data.person?.uuid, this.entity.data.customerPerson?.uuid)
        .pipe(
          map((screeningEntities) => screeningEntities[0]?.uuid),
          catchError(() => of(undefined)), // soft fail if screening entity is not found (it can happen when screening entity is not created yet)
          finalize(() => this.isLoadingScreeningEntityUuid.set(false)),
        );
    }
  }
}
